import React, { useState, useEffect } from 'react';
import { loginStudent } from '../services/authService';
import { useNavigate, Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import ForgotPasswordModal from '../components/ForgotPasswordModal';
import { containsSqlInjection, sanitizeInputForXSS } from '../utils/securityUtils';
import '../styles/LoginPage.css';

function LoginPage() {
  const [formData, setFormData] = useState({ username: '', password: '' });
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [isForgotModalOpen, setIsForgotModalOpen] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) navigate('/home');
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMessage('');
    setSuccessMessage('');

    // Güvenlik: SQL Injection taraması
    if (containsSqlInjection(formData.username) || containsSqlInjection(formData.password)) {
      setErrorMessage('şüpheli karakterler algılandı. Lütfen giriş bilgilerinizi kontrol edin.');
      setLoading(false);
      return;
    }

    // XSS Temizliği (İsteğe bağlı, ancak backend'e gidecek veri için iyi bir pratiktir)
    const sanitizedData = {
      username: sanitizeInputForXSS(formData.username),
      password: formData.password // Şifreyi sanitize etmek şifrede kullanılan < gibi karakterleri bozup girişi engelleyebilir, bu yüzden şifreye genelde sanitize yapılmaz, sadece kontrol edilir.
    };

    try {
      const response = await loginStudent(sanitizedData);

      if (response.isSuccess && response.data) {
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('username', response.data.username);
        localStorage.setItem('studentId', response.data.id);
        localStorage.setItem('isAdmin', response.data.isAdmin);

        setSuccessMessage('Giriş başarılı!');
        setTimeout(() => navigate('/home'), 1000);
      } else {
        setErrorMessage(response.responseMessage || 'Giriş başarısız');
      }
    } catch (err) {
      let message = err.message || 'Giriş başarısız oldu.';
      if (message?.toLowerCase().includes('invalid credentials')) {
        message = 'Kullanıcı adı veya şifre yanlış';
      } else if (message?.toLowerCase().includes('timeout') || message?.toLowerCase().includes('expired')) {
        message = 'Veriler yükleniyor, lütfen tekrar deneyin. Bu işlem 30 saniye kadar sürebilir.';
      }
      setErrorMessage(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Navbar />

      <main id="login-main">
        {/* LEFT: Welcome / Brand */}
        <section id="welcome-section">
          <div className="welcome-content">
            <h1 className="welcome-title">İKL Mezun Ağı</h1>
            <p id="welcome-slogan" className="welcome-slogan">
              Geçmişten geleceğe uzanan güçlü bir dayanışma.
            </p>
            <div className="ikl-history">
              <h3 className="history-title">İzmir Kız Lisesi</h3>
              <p id="ikl-history-1" className="history-paragraph">
                1916’da kurulan İzmir Kız Lisesi, ülkemizin köklü eğitim kurumlarından biridir
                ve çağdaş, üretken bireyler yetiştirme vizyonuyla tanınır.
              </p>

              <p className="history-paragraph">
                Köklü tarihi boyunca pek çok alanda iz bırakan mezunlar vermiş olan İzmir Kız Lisesi,
                akademik başarının yanı sıra sanat, kültür ve sosyal alanlarda da öncü bir rol
                üstlenmiştir. Edebiyattan bilime, sanattan spora kadar birçok alanda ulusal ve
                uluslararası başarılara imza atmış mezunları, ülkemizin gelişimine değerli katkılar
                sunmuştur.
              </p>
              <p className="history-paragraph">
                Bugün İKL Mezun Ağı olarak, bu güçlü tarihin mirasını geleceğe taşımayı,
                kuşaklar arası dayanışmayı güçlendirmeyi ve mezunlarımız arasında süregelen
                bilgi paylaşımını desteklemeyi amaçlıyoruz. Bu platform, mezunlarımızın
                birbirleriyle bağlantıda kalması, deneyimlerini paylaşması ve yeni nesillere
                rehberlik etmesi için kurulmuştur.
              </p>
            </div>

            <p id="welcome-desc" className="welcome-desc">
              Mezunlarla yeniden bağ kurun, etkinlik ve duyuruları takip edin, topluluğun
              parçası olun.
            </p>


          </div>
        </section>


        {/* RIGHT: Login + Short History */}
        <section id="login-form-section">
          <header id="login-header">
            <h2 id="login-title">Giriş Yap</h2>
          </header>

          <form id="login-form" onSubmit={handleSubmit}>
            <label htmlFor="username-input">Kullanıcı Adı</label>
            <input
              id="username-input"
              type="text"
              name="username"
              value={formData.username}
              onChange={handleChange}
              placeholder="Kullanıcı Adı / e-posta"
              required
            />

            <label htmlFor="password-input">Şifre</label>
            <input
              id="password-input"
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="Şifre"
              required
            />
            <div style={{ textAlign: 'right', marginBottom: '15px' }}>
              <span
                style={{ cursor: 'pointer', color: '#6fb98f', fontSize: '0.9rem', textDecoration: 'none' }}
                onClick={() => setIsForgotModalOpen(true)}
                onMouseOver={(e) => e.target.style.textDecoration = 'underline'}
                onMouseOut={(e) => e.target.style.textDecoration = 'none'}
              >
                Şifremi unuttum
              </span>
            </div>

            <button id="login-submit" type="submit" disabled={loading}>
              {loading ? 'Yükleniyor...' : 'Giriş Yap'}
            </button>

            {errorMessage && <p id="login-error">{errorMessage}</p>}
            {successMessage && <p id="login-success">{successMessage}</p>}

            <p id="register-text">
              Henüz hesabınız yok mu? <Link to="/register">Kayıt Ol</Link>
            </p>
          </form>


        </section>
      </main>

      {isForgotModalOpen && (
        <ForgotPasswordModal
          isOpen={isForgotModalOpen}
          onClose={() => setIsForgotModalOpen(false)}
        />
      )}

      <Footer />
    </>
  );
}

export default LoginPage;
